import { createContext, use, type Dispatch, type SetStateAction } from "react";
import { noop } from "es-toolkit";

import type { RowGeoResponse } from "api/model";

import type { BoundaryPoint } from "./geo";
import type {
  FrozenEdgeSlideConstraint,
  FrozenVertexSlideConstraint,
  GridCellSeed,
  GridEdgeRef,
  GridEdgeSlideResult,
} from "./gridMeshTypes";
import type { GridTransform, PlotGrid } from "./gridUtils";

export interface PlotMapBoundaryContext {
  /** Promote the current draft to the server baseline after a successful save. */
  acceptSaved: () => void;
  addPoint: (point: BoundaryPoint) => void;
  canRedo: boolean;
  canUndo: boolean;
  /** Abandon the draft and restore working state from the server baseline. */
  cancelDraft: () => void;
  clear: () => void;
  close: () => void;
  editingGrowerGeoId: number | null;
  editingSiteGeoId: number | null;
  endHistoryGesture: () => void;
  enterGrowerBoundaryMode: (growerGeoId: number | null) => void;
  enterSiteBoundaryMode: (siteGeoId: number) => void;
  insertPoint: (index: number, point: BoundaryPoint) => void;
  isClosed: boolean;
  isDirty: boolean;
  isGrowerBoundaryMode: boolean;
  isSiteBoundaryMode: boolean;
  label: string;
  /** Hydrate working state and server baseline from saved geometry. */
  loadSaved: (points: BoundaryPoint[], label?: string) => void;
  pauseGrowerBoundaryMode: () => void;
  points: BoundaryPoint[];
  redo: () => void;
  removePoint: (index: number) => void;
  setLabel: Dispatch<SetStateAction<string>>;
  undo: () => void;
  updatePoint: (index: number, point: BoundaryPoint) => void;
}

/**
 * Grower → site → trial scope plus the working plot grid (draft, mesh edits,
 * undo). Includes unused restore plumbing (e.g. insertVertexOnEdge).
 */
export interface PlotMapPlotContext {
  /** Promote the current draft to the server baseline after a successful save. */
  acceptSaved: () => void;
  beginHistoryGesture: () => void;
  canRedo: boolean;
  canUndo: boolean;
  /** Abandon the draft and restore working state from the server baseline. */
  cancelDraft: () => void;
  clearSelection: () => void;
  /**
   * Bake the lattice into a mesh, seeding cells with plot metadata by position.
   * `rows` splits each plot along its longer axis and assigns those strips to
   * the trial's row ids.
   */
  commit: (
    cellSeeds?: Record<string, GridCellSeed>,
    rows?: RowGeoResponse[],
  ) => void;
  current: PlotGrid | null;
  /** Implemented in provider; not invoked from mesh right-clicks (see useMeshEditInteraction). */
  deleteCell: (cellId: string) => void;
  /** Implemented in provider; not invoked from mesh right-clicks (see useMeshEditInteraction). */
  deleteEdge: (edge: GridEdgeRef) => void;
  /** Implemented in provider; not invoked from mesh right-clicks (see useMeshEditInteraction). */
  deleteVertex: (vertexId: string) => void;
  detachAndTranslateSelection: (
    cellIds: string[],
    delta: { latitude: number; longitude: number },
  ) => void;
  endHistoryGesture: () => void;
  enterPlotGridMode: () => void;
  /** Snap and dedup a single slid edge after the pointer gesture ends. */
  finalizeEdgeSlide: (edge: GridEdgeRef, chainVertexIds?: string[]) => void;
  growerGeoId: number | null;
  /** Implemented in provider; not invoked from mesh map clicks (see useMeshEditInteraction). */
  insertVertexOnEdge: (edge: GridEdgeRef, point: BoundaryPoint) => void;
  isDirty: boolean;
  isPlotGridMode: boolean;
  /** Hydrate working state and server baseline from the plots-geo query. */
  loadSaved: (grid: PlotGrid | null) => void;
  mergeSelectedCells: () => void;
  moveEdge: (
    edge: GridEdgeRef,
    delta: { latitude: number; longitude: number },
    chainVertexIds?: string[],
  ) => void;
  moveVertex: (vertexId: string, point: BoundaryPoint) => void;
  pausePlotGridMode: () => void;
  /** True after an edit gesture leaves plot interiors intersecting. */
  plotsOverlap: boolean;
  redo: () => void;
  /**
   * Replace the draft with a fresh uncommitted lattice at the site and drop
   * undo history. The saved baseline becomes empty.
   */
  resetToInitialPlacement: (
    rows: number,
    columns: number,
    plotLengthMeters: number,
    plotWidthMeters: number,
    cellSeeds?: Record<string, GridCellSeed>,
  ) => void;
  /**
   * Rotate `cellIds` around `center` by `degrees` from `origins`. When
   * `detachShared` is set and the group shares vertices with other cells,
   * those vertices are detached once and the returned origins are the
   * island's copies.
   */
  rotateContiguousCells: (
    cellIds: string[],
    center: BoundaryPoint,
    degrees: number,
    origins: Record<string, BoundaryPoint>,
    detachShared: boolean,
  ) => Record<string, BoundaryPoint>;
  /** True after an edit gesture leaves row interiors intersecting. */
  rowsOverlap: boolean;
  selectedCellIds: string[];
  selectedVertexId: string | null;
  /** Clears the site and trial, which are only meaningful under a grower. */
  selectGrower: (growerGeoId: number | null) => void;
  /** Clears the trial, which is only meaningful under a site. */
  selectSite: (siteGeoId: number | null) => void;
  selectTrial: (trialId: number | null) => void;
  setDimensions: (
    rows: number,
    columns: number,
    cellSeeds?: Record<string, GridCellSeed>,
  ) => void;
  setLabelInversion: (
    invertRowLabels: boolean,
    invertColumnLabels: boolean,
  ) => void;
  setPlotSize: (plotLengthMeters: number, plotWidthMeters: number) => void;
  setSelectedCellIds: (cellIds: string[]) => void;
  setSelectedVertexId: (vertexId: string | null) => void;
  siteGeoId: number | null;
  slideEdge: (
    edge: GridEdgeRef,
    pointer: BoundaryPoint,
    chainVertexIds?: string[],
    frozenConstraint?: FrozenEdgeSlideConstraint | null,
  ) => GridEdgeSlideResult;
  slideVertex: (
    vertexId: string,
    pointer: BoundaryPoint,
    frozenConstraint?: FrozenVertexSlideConstraint | null,
  ) => void;
  toggleCellSelection: (cellId: string) => void;
  trialId: number | null;
  undo: () => void;
  updateTransform: (transform: GridTransform) => void;
}

export interface PlotMapContextValue {
  boundary: PlotMapBoundaryContext;
  plot: PlotMapPlotContext;
  _default?: boolean;
}

const defaultBoundaryContext: PlotMapBoundaryContext = {
  acceptSaved: noop,
  addPoint: noop,
  canRedo: false,
  canUndo: false,
  cancelDraft: noop,
  clear: noop,
  close: noop,
  editingGrowerGeoId: null,
  editingSiteGeoId: null,
  endHistoryGesture: noop,
  enterGrowerBoundaryMode: noop,
  enterSiteBoundaryMode: noop,
  insertPoint: noop,
  isClosed: false,
  isDirty: false,
  isGrowerBoundaryMode: false,
  isSiteBoundaryMode: false,
  label: "",
  loadSaved: noop,
  pauseGrowerBoundaryMode: noop,
  points: [],
  redo: noop,
  removePoint: noop,
  setLabel: noop,
  undo: noop,
  updatePoint: noop,
};

const defaultPlotContext: PlotMapPlotContext = {
  acceptSaved: noop,
  beginHistoryGesture: noop,
  canRedo: false,
  canUndo: false,
  cancelDraft: noop,
  clearSelection: noop,
  commit: noop,
  current: null,
  deleteCell: noop,
  deleteEdge: noop,
  deleteVertex: noop,
  detachAndTranslateSelection: noop,
  endHistoryGesture: noop,
  enterPlotGridMode: noop,
  finalizeEdgeSlide: noop,
  growerGeoId: null,
  insertVertexOnEdge: noop,
  isDirty: false,
  isPlotGridMode: false,
  loadSaved: noop,
  mergeSelectedCells: noop,
  moveEdge: noop,
  moveVertex: noop,
  pausePlotGridMode: noop,
  plotsOverlap: false,
  redo: noop,
  resetToInitialPlacement: noop,
  rotateContiguousCells: (_cellIds, _center, _degrees, origins) => origins,
  rowsOverlap: false,
  selectedCellIds: [],
  selectedVertexId: null,
  selectGrower: noop,
  selectSite: noop,
  selectTrial: noop,
  setDimensions: noop,
  setLabelInversion: noop,
  setPlotSize: noop,
  setSelectedCellIds: noop,
  setSelectedVertexId: noop,
  siteGeoId: null,
  slideEdge: (edge, _pointer, chainVertexIds) => ({
    chainVertexIds: chainVertexIds ?? [edge.startVertexId, edge.endVertexId],
    edge,
  }),
  slideVertex: noop,
  toggleCellSelection: noop,
  trialId: null,
  undo: noop,
  updateTransform: noop,
};

const defaultContext: PlotMapContextValue = {
  boundary: defaultBoundaryContext,
  plot: defaultPlotContext,
  _default: true,
};

export const PlotMapContext = createContext(defaultContext);

export const usePlotMap = (): PlotMapContextValue => {
  const ctx = use(PlotMapContext);

  if (ctx._default) {
    console.warn("usePlotMap() must be used within a <PlotMapProvider />");
  }

  return ctx;
};
